import React, { useCallback, useMemo, useState } from "react";
import {
  Grid,
  Link,
  Typography,
  Box,
  Checkbox,
  makeStyles,
  Avatar,
  IconButton,
  Badge
} from "@material-ui/core";
import { Droppable } from "react-beautiful-dnd";
import { connect } from "react-redux";
import {
  distanceStr,
  getDateValue,
  getRouteUnitsLabel,
  hoursMinsFromSeconds,
  remCalc,
  secondsToTime
} from "../../utils/helpers";
import clsx from "clsx";
import LockOpenIcon from "@material-ui/icons/LockOpen";
import LockIcon from "@material-ui/icons/Lock";
import OptimizedTooltip from "../common/OptimizedTooltip";
import { setInfoDialog } from "../../store/actions/common";
import { getCookie } from "../../utils/cookies";
import { routeColors } from "../data/constants";
import useProductTour, {
  PRODUCT_TOUR_STEP_IDS
} from "../../states/tour/tours/product";
import DirectionsCarIcon from "@material-ui/icons/DirectionsCar";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import ReplyIcon from "@material-ui/icons/Reply";
import {
  setSelectedRoutes,
  setShowingRouteId
} from "../../store/actions/project";
import { FormattedText } from "../common/FormattedText";
import CountIndicatorIcon from "../common/CountIndicatorIcon";
import AssignDriverMenu from "./AssignDriverMenu";
import DateFnsUtils from "@date-io/date-fns";
import useRouteData from "../../hooks/useRouteInfo";

const dateFns = new DateFnsUtils();
const useStyles = makeStyles(theme => ({
  wrapper: {
    minHeight: 56,
    borderBottom: "1px solid #EAEBF0",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    width: "100%",
    flexWrap: "nowrap",
    overflow: "hidden",
    cursor: "pointer",
    "& .show-on-hover": {
      display: "none"
    },
    "& .hide-on-hover": {
      display: "block"
    },
    "&:hover": {
      background: "#F4F4F4",
      "& .show-on-hover": {
        display: "block"
      },
      "& .hide-on-hover": {
        display: "none"
      }
    }
  },
  active: {
    background: "#F4F4F4"
  },
  truckIconWrapper: {
    boxSizing: "border-box",
    position: "relative",
    flexShrink: 0,
    width: 64,
    height: "100%",
    paddingLeft: theme.spacing(3),
    justifyContent: "center"
  },
  truckIconLink: {
    boxSizing: "border-box",
    display: "block",
    width: 24,
    height: 24,
    borderRadius: 4,
    paddingTop: 3.5,
    paddingLeft: 5
  },
  truckIcon: {
    color: "#fff",
    fontSize: theme.typography.pxToRem(14)
  },
  chipWrapper: {
    flex: "1 1 100%",
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(0.375),

    "& button": {
      display: "flex",
      alignItems: "center",

      "& svg": {
        color: "#808080"
      }
    },

    "& p": {
      fontSize: remCalc(12),
      marginLeft: theme.spacing(0.5),
      fontStyle: "italic"
    }
  },
  driverChip: {
    height: remCalc(28),
    marginRight: theme.spacing(1),
    maxWidth: "80%",
    overflow: "hidden",
    padding: theme.spacing(0, 1.5),
    "& .MuiChip-label": {
      padding: 0,
      fontSize: "0.875rem",
      textOverflow: "ellipsis",
      maxWidth: "100%",
      overflow: "hidden",
      display: "inline"
    },
    "& svg": {
      width: theme.spacing(2.25),
      height: theme.spacing(2.25),
      margin: theme.spacing(0, -0.5, 0, 0.5)
    }
  },
  chipStatusIcon: {
    color: "#C4C4C4",
    marginLeft: theme.spacing(0.5)
  },
  checkedIcon: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    "& img": {
      width: "100%",
      display: "block"
    }
  },
  routeDetails: {
    display: "flex",
    alignItems: "flex-start",
    height: "100%",
    overflow: "hidden"
  },
  liveIndicator: {
    color: "#1DA25E",
    fontSize: remCalc(11),
    textTransform: "uppercase",
    minWidth: remCalc(40),
    padding: theme.spacing(0, 1),
    height: remCalc(18),
    boxSizing: "border-box",
    borderRadius: 4,
    border: "1px solid #1DA25E"
  },
  liveFailed: {
    color: theme.palette.error.main,
    border: `1px solid ${theme.palette.error.main}`,
    textDecoration: "line-through"
  },
  routeCompleted: {
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.text.secondary}`
  },
  routeCheckbox: {
    padding: 0,
    backgroundColor: "transparent",
    "& svg": {
      color: "#DADADA",
      width: theme.spacing(2.5),
      height: theme.spacing(2.5)
    }
  },
  eyeIcon: {
    height: remCalc(20)
  },
  btnIconLight: {
    "&:not(:last-child)": {
      marginRight: theme.spacing(1.25)
    }
  },
  lockIcon: {
    marginLeft: theme.spacing(2.25),
    "& svg": {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5)
    }
  },
  driverLink: {
    marginRight: -16,
    width: 40,
    height: 40,
    borderRadius: "100%",
    "&:hover": {
      background: "rgba(32, 33, 36, 0.08)"
    },
    "& svg": {
      transform: "rotateY(180deg)"
    }
  },
  driverLinkDisabled: {
    cursor: "default",
    "& svg": { color: "#5F6368 !important" }
  },
  fontWeightBold: {
    fontWeight: 700
  },
  text14: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: "1.125rem"
  },
  invisible: {
    color: "#d93024"
  },
  visible: {
    color: "#c4c4c4"
  },
  routeLabel: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    paddingRight: "12px",
    marginBottom: "4px"
  },
  driverAvatarButton: {
    padding: 7
  },
  driverAvatar: {
    width: 26,
    height: 26,
    backgroundColor: "#1329FE",
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium
  },
  noDriverAvatarIcon: {
    fontSize: theme.typography.pxToRem(26),
    color: "#C2C3C7"
  },
  actionsWrapper: {
    minWidth: 100
  },
  timeStamps: {
    fontSize: theme.typography.pxToRem(14),
    color: "#4D4D50"
  },
  checkBox: {
    padding: 8,
    margin: theme.spacing(-1)
  },
  liveStatusBadge: {
    top: "17%",
    right: "17%"
  },
  liveStatusBadgeColorSecondary: {
    backgroundColor: "#1DA25E"
  },
  show: {
    display: "block !important"
  },
  hide: {
    display: "none !important"
  }
}));

function RouteDetails({
  isPastProject,
  published,
  backhaulGroup,
  driversLiveStatus,
  distanceUnits,
  driversOpen,
  colorsObj,
  handleRouteLock,
  toggleRouteSelectedStatus,
  shareRoute,
  plan,
  routeIsSelected,
  setInfoDialog,
  publishedOnce,
  hasSelectedRoutes,
  driverDragging,
  setShowingRouteId,
  searchInputValue,
  setSelectedRoutes,
  driverLocations,
  activeByListCommand,
  setHaveHovered = () => {},
  haveHovered,
  index
}) {
  const classes = useStyles();
  const productTour = useProductTour();
  const [assignDriverAnchorElement, setAssignDriverAnchorElement] =
    useState(null);

  const getColor = id => (colorsObj[id] >= 0 ? colorsObj[id] : 0);

  // const allRouteIds = backhaulGroup.map(routeGroup => routeGroup.id);
  // const allStops = backhaulGroup.map(routeGroup => routeGroup.stops).flat();

  const {
    allRouteIds,
    allStops,
    backhaulGroupCount,
    unitsCount,
    startEndStops,
    linkDisabled,
    distances,
    completionTime,
    allNonStartEndBreakStops,
    mainRoute
  } = useRouteData(backhaulGroup);

  // const unitsCount = allStops.reduce(
  //   (previousCount, stop) =>
  //     previousCount +
  //     stop.units?.reduce((count, unit) => count + unit.quantity, 0),
  //   0
  // );

  // const allNonStartEndStops = allStops.filter(
  //   stop => stop.type !== "start" && stop.type !== "end"
  // );
  // const allStarts = allStops.filter(stop => stop.type === "start");
  // const allEnds = allStops.filter(stop => stop.type === "end");
  // const linkDisabled = !allStops.filter(
  //   task => !["start", "end"].includes(task.type)
  // ).length;

  // const startEndStops =
  //   allNonStartEndStops.length > 0 &&
  //   allStops.length !== allNonStartEndStops.length
  //     ? [allStarts[0], allEnds[allStarts.length - 1]]
  //     : null;

  const live = driversLiveStatus
    ? Object.keys(driversLiveStatus).some(routeId =>
        allRouteIds.includes(routeId)
      )
    : false;

  const allDriverDates = Object.keys(driverLocations)
    .filter(routeId => allRouteIds.includes(routeId))
    .map(routeId => {
      const dateObj = new Date(getDateValue(driverLocations[routeId].date));
      return dateObj.getTime();
    });

  let lastDriverSeenDate =
    allDriverDates.length > 0 ? Math.max(...allDriverDates) : null;

  const isisProductTourPartPublishAndShareStepShareLink =
    productTour.isTourStepId(
      PRODUCT_TOUR_STEP_IDS.PART_PUBLISH_AND_SHARE_STEP_SHARE_LINK
    );

  const handleDriverAssignMenuClose = useCallback(
    () => setAssignDriverAnchorElement(null),
    []
  );

  const unitsCountLabel = getRouteUnitsLabel(unitsCount);

  return (
    <>
      <Droppable
        droppableId={`panel-${mainRoute.id}`}
        // Allow drop a driver on route info box (assign to route)
        isDropDisabled={!driverDragging}
      >
        {provided => {
          return (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              <Grid
                id={`route_details_${index}`}
                container
                key={mainRoute.driver || driversOpen}
                alignItems="center"
                data-cy="routeDetails"
                onMouseEnter={() => {
                  setHaveHovered(true);
                }}
                onMouseLeave={() => {
                  setHaveHovered(false);
                }}
                className={clsx(
                  classes.wrapper,
                  activeByListCommand && !haveHovered && classes.active,
                  "route-details"
                )}
                onClick={() => {
                  if (!isisProductTourPartPublishAndShareStepShareLink) {
                    setSelectedRoutes([]);
                    setShowingRouteId(mainRoute.id);
                  }
                }}
              >
                {/* Truck icon */}
                <Grid
                  item
                  xs="auto"
                  className={clsx(classes.truckIconWrapper, "truck-icon")}
                >
                  <div
                    className={clsx(
                      "show-on-hover",
                      hasSelectedRoutes && classes.show
                    )}
                  >
                    <OptimizedTooltip title="Select">
                      <Checkbox
                        className={classes.checkBox}
                        checked={routeIsSelected}
                        onChange={() => {
                          if (
                            !isisProductTourPartPublishAndShareStepShareLink
                          ) {
                            toggleRouteSelectedStatus(mainRoute.id);
                          }
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                        value="checkedB"
                        color="primary"
                      />
                    </OptimizedTooltip>
                  </div>
                  <Box
                    className={clsx(
                      classes.truckIconLink,
                      "hide-on-hover",
                      hasSelectedRoutes && classes.hide
                    )}
                    style={{
                      backgroundColor:
                        routeColors[getColor(mainRoute.id) % routeColors.length]
                    }}
                  >
                    <DirectionsCarIcon className={classes.truckIcon} />
                  </Box>
                </Grid>
                <Grid item style={{ overflow: "hidden", flexGrow: "1" }}>
                  <div style={{ display: "flex" }}>
                    {/* Route name and info */}
                    <div className={classes.routeDetails}>
                      <div style={{ width: "100%" }}>
                        <Box display="flex" alignItems="center" pr={1}>
                          <Typography
                            className={clsx(classes.text14, classes.routeLabel)}
                          >
                            {mainRoute.locked && (
                              <LockIcon
                                style={{ fontSize: 14, verticalAlign: -2.5 }}
                              />
                            )}{" "}
                            <FormattedText
                              text={mainRoute.vehicle_name || "No label"}
                              query={searchInputValue}
                            />
                          </Typography>
                          {/*
                  {plan &&
                    plan.ft_realtime_driver_tracking &&
                    // published &&
                    (live || route.stops[0].status === "finished" ? (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="fontWeightBold"
                        flexShrink={0}
                        className={clsx(classes.liveIndicator, {
                          [classes.liveFailed]:
                            !live &&
                            route.stops[route.stops.length - 1].status !==
                              "finished",
                          [classes.routeCompleted]:
                            route.stops[route.stops.length - 1].status ===
                            "finished",
                          [classes.hide]:
                            isPastProject &&
                            route.stops[route.stops.length - 1].status !==
                              "finished"
                        })}
                      >
                        {route.stops[route.stops.length - 1].status ===
                        "finished"
                          ? "Completed"
                          : "Live"}
                      </Box>
                    ) : null)}
                    */}
                        </Box>
                        <Typography
                          color="secondary"
                          variant="body2"
                          style={{ fontSize: 13 }}
                        >
                          {
                            allNonStartEndBreakStops.length
                          }{" "}
                          stops •{" "}
                          {distanceStr(distances, distanceUnits === "km")} •{" "}
                          {hoursMinsFromSeconds(completionTime)}
                          {unitsCountLabel}
                        </Typography>
                      </div>
                    </div>

                    {/* Route actions */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={"flex-end"}
                      ml="auto"
                      mr={1.75}
                      className={classes.actionsWrapper}
                      onClick={e => e.stopPropagation()}
                    >
                      {startEndStops && (
                        <div
                          className={clsx(
                            "hide-on-hover",
                            isisProductTourPartPublishAndShareStepShareLink &&
                              classes.hide
                          )}
                        >
                          <CountIndicatorIcon
                            count={backhaulGroupCount}
                            style={{ marginRight: 8, marginTop: 2 }}
                          />
                          <Typography
                            className={classes.timeStamps}
                          >{`${secondsToTime(
                            startEndStops[0]?.finish_time ||
                              startEndStops[0]?.end_time
                          )}-${secondsToTime(
                            startEndStops[1]?.arr_time
                          )}`}</Typography>
                        </div>
                      )}
                      <div
                        className={clsx(
                          "show-on-hover",
                          isisProductTourPartPublishAndShareStepShareLink &&
                            classes.show
                        )}
                      >
                        {/* Driver link */}
                        {!linkDisabled &&
                          (published || (!published && publishedOnce)) && (
                            <OptimizedTooltip
                              placement="bottom"
                              title={
                                isPastProject
                                  ? "Project is outdated"
                                  : mainRoute.driver
                                  ? "Send to driver"
                                  : "Share route"
                              }
                            >
                              <Link
                                component="button"
                                className={clsx(
                                  classes.driverLink,
                                  "driver-share-link",
                                  {
                                    [classes.driverLinkDisabled]: linkDisabled
                                  }
                                )}
                                onClick={() => {
                                  /*if (isPastProject) {
                                      e.preventDefault();
                                    } else {*/
                                  if (
                                    isisProductTourPartPublishAndShareStepShareLink
                                  ) {
                                    productTour.goToNext();
                                  }
                                  shareRoute(
                                    allRouteIds
                                  );
                                  //}
                                }}
                                disabled={linkDisabled}
                                data-cy="sendRouteToDriver"
                              >
                                <ReplyIcon fontSize="small" />
                              </Link>
                            </OptimizedTooltip>
                          )}
                        {/* Lock/unlock route */}
                        {!published && (
                          <OptimizedTooltip
                            placement="bottom"
                            title={
                              mainRoute.locked ? "Unlock route" : "Lock route"
                            }
                          >
                            <Link
                              component="button"
                              className={clsx(classes.lockIcon, {
                                "no-hover-color": mainRoute.locked
                              })}
                              data-cy={
                                mainRoute.locked
                                  ? "unlockRouteBtn"
                                  : "lockRouteBtn"
                              }
                              onClick={() => {
                                if (
                                  mainRoute.locked ||
                                  getCookie("dontShowLockDialog")
                                ) {
                                  handleRouteLock(
                                    mainRoute.id,
                                    mainRoute.vehicle_id,
                                    mainRoute.locked
                                  );
                                } else {
                                  setInfoDialog({
                                    visible: true,
                                    checkBox: true,
                                    title: "Lock route",
                                    body: "You can lock routes to prevent them from being changed. For example, if you click on the reoptimize button, all the remaining routes will be reoptimized without changing your locked routes.",
                                    action: () =>
                                      handleRouteLock(
                                        mainRoute.id,
                                        mainRoute.vehicle_id,
                                        mainRoute.locked
                                      )
                                  });
                                }
                              }}
                            >
                              {mainRoute.locked ? (
                                <LockOpenIcon
                                  fontSize="small"
                                  style={{
                                    color: "#606165"
                                  }}
                                />
                              ) : (
                                <LockIcon />
                              )}
                            </Link>
                          </OptimizedTooltip>
                        )}

                        {/*
                  // Toggle route
                  <OptimizedTooltip
                    placement="bottom"
                    title={
                      hiddenRoutes.includes(route.id)
                        ? "Show route"
                        : "Hide route"
                    }
                  >
                    <Link
                      component="button"
                      className={clsx(classes.btnIconLight, classes.eyeIcon, {
                        "no-hover-color": hiddenRoutes.includes(route.id)
                      })}
                      data-cy="toggleRouteVisibility"
                      onClick={() => toggleRoute(route.id)}
                    >
                      {hiddenRoutes.includes(route.id) ? (
                        <InvisibleIcon
                          fontSize="small"
                          className={classes.invisible}
                        />
                      ) : (
                        <VisibleIcon
                          fontSize="small"
                          className={classes.visible}
                        />
                      )}
                    </Link>
                  </OptimizedTooltip>
                   */}
                        {/*
                  {!published && (
                    <Checkbox
                      checked={routeIsSelected}
                      onChange={() => toggleRouteSelectedStatus(route.id)}
                      className={classes.routeCheckbox}
                      value="checkedB"
                      color="primary"
                      inputProps={{
                        "aria-label": "secondary checkbox",
                        "data-cy": "toggleRouteSelectedStatus"
                      }}
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={
                        <Box
                          className={classes.checkedIcon}
                          display="inline-flex"
                        >
                          <img src={CheckIcon} alt="Checked" />
                        </Box>
                      }
                    />
                  )}
                  */}
                      </div>
                    </Box>
                    {/* Route driver */}
                    <Box
                      display="flex"
                      alignItems="center"
                      mr={2}
                      onClick={e => e.stopPropagation()}
                    >
                      <OptimizedTooltip
                        placement="bottom"
                        title={
                          mainRoute.driver ? (
                            <>
                              Assigned to {mainRoute.driver.first_name || ""}{" "}
                              {mainRoute.driver.last_name || ""}
                              {allStops[0].status === "finished" &&
                                !live &&
                                lastDriverSeenDate && (
                                  <>
                                    <br />
                                    Last seen at
                                    {dateFns.format(
                                      new Date(lastDriverSeenDate),
                                      "H:m, d MMM"
                                    )}
                                  </>
                                )}
                            </>
                          ) : published ? (
                            "Route unassigned"
                          ) : (
                            "Assign driver"
                          )
                        }
                      >
                        <span>
                          <IconButton
                            disabled={published}
                            className={classes.driverAvatarButton}
                            onClick={event => {
                              if (
                                !isisProductTourPartPublishAndShareStepShareLink
                              ) {
                                setAssignDriverAnchorElement(
                                  event.currentTarget
                                );
                              }
                            }}
                          >
                            <Badge
                              invisible={
                                !(plan && plan.ft_realtime_driver_tracking) ||
                                !(live || allStops[0].status === "finished") ||
                                isPastProject ||
                                allStops[allStops.length - 1].status ===
                                  "finished"
                              }
                              classes={{
                                badge: classes.liveStatusBadge,
                                colorSecondary:
                                  classes.liveStatusBadgeColorSecondary
                              }}
                              overlap="circle"
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right"
                              }}
                              variant="dot"
                              color={
                                live || allStops[0].status === "finished"
                                  ? live
                                    ? "secondary"
                                    : "error"
                                  : ""
                              }
                            >
                              {mainRoute.driver ? (
                                <Avatar className={classes.driverAvatar}>
                                  {`${
                                    mainRoute.driver.first_name
                                      ? mainRoute.driver.first_name[0]
                                      : ""
                                  }${
                                    mainRoute.driver.last_name
                                      ? mainRoute.driver.last_name[0]
                                      : ""
                                  }`.toUpperCase()}
                                </Avatar>
                              ) : (
                                <AccountCircleIcon
                                  className={classes.noDriverAvatarIcon}
                                />
                              )}
                            </Badge>
                          </IconButton>
                        </span>
                      </OptimizedTooltip>
                    </Box>
                  </div>
                  {/*
                  {driversOpen && (
                    <Box display="flex" className={classes.chipWrapper}>
                      <Chip
                        label={`Drop to assign`}
                        onClick={e => e.stopPropagation()}
                        className={clsx(classes.driverChip)}
                        color={isDraggingOver ? "primary" : "default"}
                        data-cy="driverDroppableChip"
                      />
                    </Box>
                  )}*/}
                  {/* Driver chip
          {route.driver ? (
            <Box
              display="flex"
              alignItems="center"
              className={classes.chipWrapper}
            >
              // Chip
              <Chip
                label={`${route.driver.first_name || ""} ${
                  route.driver.last_name || ""
                }`}
                data-cy="driverChip"
                onClick={e => e.stopPropagation()}
                onDelete={published ? null : () => removeDriver(route.id)}
                className={classes.driverChip}
                color="primary"
              />

              {published && !isPastProject && (
                <>
                  // Resend 
                  <OptimizedTooltip
                    placement="bottom"
                    title={
                      isPastProject
                        ? "Project is outdated"
                        : canResend(route.message_sent_at)
                        ? "Resend email to driver"
                        : "Please wait 1 minute to resend"
                    }
                  >
                    <Link
                      component="button"
                      onClick={e => {
                        if (isPastProject) {
                          e.preventDefault();
                        } else {
                          resendToDriver(e, route.id, route.message_sent_at);
                        }
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </Link>
                  </OptimizedTooltip>

                  {(route.message_sent_at || route.seen_at) && (
                    <>
                      // Dispatched / Seen 
                      <OptimizedTooltip
                        placement="bottom"
                        title={
                          route.seen_at
                            ? "Seen"
                            : route.message_sent_at && "Dispatched"
                        }
                      >
                        <Box display="flex" alignItems="center">
                          <MobileCheckedIcon
                            checked={route.seen_at}
                            fontSize="small"
                            className={classes.chipStatusIcon}
                          />
                        </Box>
                      </OptimizedTooltip>

                      // Timestamp 
                      <Typography variant="body2" color="secondary">
                        {route.seen_at
                          ? `Seen at ${parseDateStr(route.seen_at)}`
                          : route.message_sent_at &&
                            `Dispatched at ${parseDateStr(
                              route.message_sent_at
                            )}`}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          ) : (
            driversOpen && (
              <Box display="flex" className={classes.chipWrapper}>
                <Chip
                  label={`Drop to assign`}
                  onClick={e => e.stopPropagation()}
                  className={clsx(classes.driverChip)}
                  color={isDraggingOver ? "primary" : "default"}
                  data-cy="driverDroppableChip"
                />
              </Box>
            )
          )} */}
                </Grid>
              </Grid>
            </div>
          );
        }}
      </Droppable>
      <AssignDriverMenu
        anchorElement={assignDriverAnchorElement}
        mainRouteId={mainRoute.id}
        onClose={handleDriverAssignMenuClose}
      />
    </>
  );
}

const mapStateToProps = state => ({
  hiddenRoutes: state.map.hiddenRoutes,
  live: state.map.driversLiveStatus,
  distanceUnits: state.project.vrp.configuration.distance_units,
  driversOpen: state.extendable.type === "drivers",
  colorsObj: state.project.solution && state.project.solution.routeColors,
  driverDragging: state.common.draggingType === "drivers",
  publishedOnce: state.project.published_once,
  driversLiveStatus: state.map.driversLiveStatus,
  driverLocations: state.map.driverLocations,
  plan:
    (state.auth.userData.organisationSite &&
      state.auth.userData.organisationSite.organisation.plan) ||
    {}
});

export default connect(mapStateToProps, {
  setInfoDialog,
  setShowingRouteId,
  setSelectedRoutes
})(RouteDetails);
